import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { authClient } from "../lib/authClient";

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Pega o email do state passado pela página de perfil
    const stateEmail = location.state?.email;

    if (stateEmail) {
      setEmail(stateEmail);
    } else {
      // Se não houver email, redireciona para login
      navigate("/login", { replace: true });
    }
  }, [location.state, navigate]);

  const handleOtpChange = (index: number, value: string) => {
    // Permite apenas números
    if (value && !/^\d+$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Pega apenas o último dígito
    setOtp(newOtp);
    setError(null);

    // Move para o próximo input se houver valor
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    // Move para o input anterior ao pressionar backspace
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    // Verifica se o conteúdo colado são apenas números
    if (!/^\d+$/.test(pastedData)) {
      return;
    }

    // Distribui os dígitos nos inputs
    const digits = pastedData.slice(0, 6).split("");
    const newOtp = [...otp];
    digits.forEach((digit, index) => {
      if (index < 6) {
        newOtp[index] = digit;
      }
    });
    setOtp(newOtp);

    // Foca no próximo input vazio ou no último
    const nextEmptyIndex = newOtp.findIndex((val) => !val);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const otpCode = otp.join("");

    if (otpCode.length !== 6) {
      setError("Por favor, insira o código completo de 6 dígitos.");
      return;
    }

    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    try {
      // Chamada para verificar o email com o código OTP
      const { error: verifyError } = await authClient.emailOtp.verifyEmail({
        email,
        otp: otpCode,
      });

      if (verifyError) {
        setError(verifyError.message ?? "Código inválido ou expirado.");
        return;
      }

      setSuccess("Email verificado com sucesso! Redirecionando...");

      // Notifica mudança na sessão para atualizar o status
      authClient.$store.notify("$sessionSignal");

      // Aguarda 1.5 segundos antes de redirecionar
      setTimeout(() => {
        navigate("/profile", { replace: true });
      }, 1500);
    } catch (error) {
      console.error("Erro ao verificar email:", error);
      setError("Erro ao verificar código. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setSuccess(null);
    setIsResending(true);

    try {
      // Reenvia o código de verificação OTP
      const { error: resendError } = await authClient.emailOtp
        .sendVerificationOtp({
          email,
          type: "email-verification",
        });

      if (resendError) {
        setError(resendError.message ?? "Erro ao reenviar código.");
        return;
      }

      setSuccess("Código reenviado! Verifique seu email.");
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch (error) {
      console.error("Erro ao reenviar código:", error);
      setError("Erro ao reenviar código. Tente novamente.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="page-centered">
      <div className="auth-card">
        <h1>Verificar Email</h1>
        <p className="auth-subtitle">
          Digite o código de 6 dígitos enviado para <strong>{email}</strong>
        </p>

        <form className="verify-form" onSubmit={handleSubmit}>
          <div className="otp-inputs" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="otp-input"
                autoFocus={index === 0}
              />
            ))}
          </div>

          {error && <p className="auth-error">{error}</p>}
          {success && <p className="auth-success">{success}</p>}

          <button
            type="submit"
            className="primary-button"
            disabled={isSubmitting || otp.join("").length !== 6}
          >
            {isSubmitting ? "Verificando..." : "Verificar Código"}
          </button>
        </form>

        <div className="verify-actions">
          <p className="resend-text">
            Não recebeu o código?{" "}
            <button
              type="button"
              onClick={handleResendCode}
              className="link-button"
              disabled={isResending}
            >
              {isResending ? "Reenviando..." : "Reenviar código"}
            </button>
          </p>

          <button
            type="button"
            onClick={() => navigate("/login")}
            className="link-button back-link"
          >
            ← Voltar para login
          </button>
        </div>
      </div>
    </div>
  );
}
